import { useState, useEffect, useMemo } from 'react'
import { X, Search } from 'lucide-react'
import { ColDef } from 'ag-grid-community'
import Button from '@/components/ui/Button'
import DataGrid from '@/components/data/DataGrid'
import Tag from '@/components/ui/Tag'
import { designTokens } from '@/utils/design-tokens'
import { OrgMemberService } from '@/api/generated/services/OrgMemberService'
import type { OrgMemberResponse } from '@/api/generated/models/OrgMemberResponse'
import { useAlert } from '@/components/ui/Alert/exports'
import { OrgMemberWorkspaceCreateRequest } from '@/api'

interface MemberAddModalProps {
  isOpen: boolean
  onClose: () => void
  onSuccess?: () => void
  workspaceId: string
  orgId: string
}

export default function MemberAddModal({
  isOpen,
  onClose,
  onSuccess,
  workspaceId,
  orgId
}: MemberAddModalProps) {
  const [searchValue, setSearchValue] = useState('')
  const [availableMembers, setAvailableMembers] = useState<OrgMemberResponse[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [selectedMembers, setSelectedMembers] = useState<string[]>([])
  const [isAdding, setIsAdding] = useState(false)

  const { showError, showSuccess } = useAlert()

  // Load available members when modal opens
  useEffect(() => {
    if (isOpen && orgId) {
      loadAvailableMembers()
    }
  }, [isOpen, orgId])

  // Reset states when modal closes
  useEffect(() => {
    if (!isOpen) {
      setSearchValue('')
      setAvailableMembers([])
      setSelectedMembers([])
      setIsLoading(false)
      setIsAdding(false)
    }
  }, [isOpen])

  const loadAvailableMembers = async () => {
    setIsLoading(true)
    try {
      // Get all organization members
      const response = await OrgMemberService.appsOrganizationViewsListOrgMembers({
        orgId: orgId,
        q: searchValue || undefined,
        page: 1,
        perPage: 1000
      })

      if (response.data) {
        // Filter out members who are already in this workspace
        const allMembers = response.data as OrgMemberResponse[]
        const membersNotInWorkspace = allMembers.filter(member => {
          // Check if member is already in this workspace
          return !member.workspaces?.some(workspace => workspace.id === workspaceId)
        })
        setAvailableMembers(membersNotInWorkspace)
      }
    } catch (error) {
      console.error('Failed to load members:', error)
      showError('멤버 목록을 불러오는 중 오류가 발생했습니다.')
    } finally {
      setIsLoading(false)
    }
  }

  const handleSearch = () => {
    loadAvailableMembers()
  }

  const handleAddMembers = async () => {
    if (selectedMembers.length === 0) {
      showError('추가할 멤버를 선택해주세요.')
      return
    }

    setIsAdding(true)

    try {
      // Add members one by one (could be optimized with bulk API if available)
      const addPromises = selectedMembers.map(memberId => {
        const addRequest: OrgMemberWorkspaceCreateRequest = {
          member_id: memberId,
          workspace_id: workspaceId
        }
        return OrgMemberService.appsOrganizationViewsOrgMemberAddWorkspace({
          requestBody: addRequest
        })
      })

      await Promise.all(addPromises)

      showSuccess(`${selectedMembers.length}명의 멤버가 워크스페이스에 추가되었습니다.`)
      onSuccess?.()
      onClose()
    } catch (error: any) {
      console.error('Failed to add members:', error)

      if (error && typeof error === 'object' && 'status' in error) {
        switch (error.status) {
          case 400:
            showError('잘못된 요청입니다. 멤버 정보를 확인해주세요.')
            break
          case 401:
            showError('권한이 없습니다.')
            break
          case 404:
            showError('멤버 또는 워크스페이스를 찾을 수 없습니다.')
            break
          case 409:
            showError('이미 워크스페이스에 추가된 멤버가 있습니다.')
            break
          case 500:
            showError('서버 오류가 발생했습니다. 잠시 후 다시 시도해주세요.')
            break
          default:
            showError('멤버 추가에 실패했습니다. 다시 시도해주세요.')
        }
      } else {
        showError('멤버 추가에 실패했습니다. 다시 시도해주세요.')
      }
    } finally {
      setIsAdding(false)
    }
  }

  const handleClose = () => {
    if (!isAdding) {
      onClose()
    }
  }

  // AG-Grid column definitions
  const columnDefs: ColDef[] = useMemo(() => [
    {
      headerName: '',
      field: 'selected',
      width: 50,
      pinned: 'left',
      checkboxSelection: true,
      headerCheckboxSelection: true,
      suppressHeaderMenuButton: true,
      suppressHeaderFilterButton: true,
      sortable: false,
      filter: false,
      resizable: false
    },
    {
      headerName: '상태',
      field: 'available',
      width: 120,
      cellRenderer: (params: any) => (
        <div className="flex items-center h-full">
          <Tag
            color={params.value ? "success" : "gray"}
            size="small"
          >
            ● {params.value ? '활성' : '비활성'}
          </Tag>
        </div>
      )
    },
    {
      headerName: '이름',
      field: 'account_name',
      flex: 1,
      minWidth: 150
    },
    {
      headerName: '이메일',
      field: 'account_email',
      flex: 2,
      minWidth: 200
    },
    {
      headerName: '전화번호',
      field: 'account_phone',
      flex: 1,
      minWidth: 150,
      cellRenderer: (params: any) => params.value || '-'
    }
  ], [])

  if (!isOpen) return null

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center"
      data-modal-overlay
      onMouseDown={(e) => e.stopPropagation()}
    >
      {/* Background Blur Overlay */}
      <div
        className="absolute inset-0"
        style={{ backgroundColor: 'rgba(0, 0, 0, 0.2)' }}
        onClick={handleClose}
      />

      {/* Modal Content */}
      <div
        className="relative bg-white rounded w-[800px] max-w-[90vw] max-h-[90vh] overflow-hidden border flex flex-col"
        style={{
          borderColor: designTokens.colors.gray[1],
          height: '700px'
        }}
      >
        {/* Header */}
        <div className="flex items-center justify-between gap-[10px] p-6 border-b flex-shrink-0">
          <h2
            className="font-bold"
            style={{
              fontFamily: designTokens.typography.fontFamily.sans[0],
              fontSize: designTokens.typography.fontSize['title-medium'][0],
              lineHeight: designTokens.typography.fontSize['title-medium'][1].lineHeight,
              fontWeight: designTokens.typography.fontSize['title-medium'][1].fontWeight,
              color: designTokens.colors.gray[6]
            }}
          >
            워크스페이스에 멤버 추가
          </h2>
          <button
            type="button"
            onClick={handleClose}
            className="w-6 h-6 flex items-center justify-center hover:opacity-70 transition-opacity"
            style={{ color: designTokens.colors.gray[5] }}
            disabled={isAdding}
          >
            <X className="w-3 h-3" strokeWidth={2} />
          </button>
        </div>

        {/* Search Section */}
        <div className="p-6 border-b flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="relative flex-1">
              <div
                className="flex items-center gap-1.5 px-3 py-0 h-10 border-2 rounded transition-all duration-150"
                style={{
                  backgroundColor: designTokens.colors.white,
                  borderColor: designTokens.colors.gray[1]
                }}
              >
                <Search
                  className="w-5 h-5"
                  style={{ color: designTokens.colors.gray[3] }}
                />
                <input
                  type="text"
                  placeholder="멤버를 검색해주세요."
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.value)}
                  className="flex-1 bg-transparent outline-none"
                  style={{
                    fontFamily: designTokens.typography.fontFamily.sans[0],
                    fontSize: designTokens.typography.fontSize['body-medium'][0],
                    lineHeight: designTokens.typography.fontSize['body-medium'][1].lineHeight,
                    color: searchValue ? designTokens.colors.gray[6] : designTokens.colors.gray[3]
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleSearch()
                    }
                  }}
                  disabled={isLoading || isAdding}
                />
              </div>
            </div>
            <Button
              color="dark"
              size="medium"
              onClick={handleSearch}
              disabled={isLoading || isAdding}
              className="h-10 px-[10px]"
            >
              검색
            </Button>
          </div>
        </div>

        {/* Members List */}
        <div className="flex-1 min-h-0 p-6">
          <div className="bg-white rounded-lg border border-gray-200 h-full">
            {isLoading ? (
              <div className="flex items-center justify-center h-full">
                <p className="text-gray-500">멤버 목록을 불러오는 중...</p>
              </div>
            ) : availableMembers.length === 0 ? (
              <div className="flex items-center justify-center h-full">
                <div className="text-center">
                  <p className="text-gray-600">추가할 수 있는 멤버가 없습니다.</p>
                  <p className="text-gray-500 mt-2">모든 멤버가 이미 워크스페이스에 포함되어 있습니다.</p>
                </div>
              </div>
            ) : (
              <DataGrid
                columnDefs={columnDefs}
                rowData={availableMembers}
                headerHeight={40}
                rowHeight={42}
                pagination={true}
                paginationPageSize={10}
                paginationPageSizeSelector={[10, 20, 50]}
                height="100%"
                suppressRowClickSelection={false}
                rowSelection="multiple"
                rowMultiSelectWithClick={true}
                onSelectionChanged={(event) => {
                  const selectedNodes = event.api.getSelectedNodes()
                  const selectedIds = selectedNodes.map(node => node.data.id)
                  setSelectedMembers(selectedIds)
                }}
              />
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t flex-shrink-0">
          <div className="text-sm text-gray-600">
            {selectedMembers.length}명의 멤버 선택됨
          </div>
          <div className="flex items-center gap-3">
            <Button
              color="light"
              size="medium"
              onClick={handleClose}
              disabled={isAdding}
              className="px-[10px] h-10"
            >
              취소
            </Button>
            <Button
              color="dark"
              size="medium"
              onClick={handleAddMembers}
              disabled={isAdding || selectedMembers.length === 0}
              className="px-[10px] h-10"
            >
              {isAdding ? '추가 중...' : `${selectedMembers.length}명 추가`}
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}